import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { stockService } from "../services/service/stockService";

const EMPTY_PARAMS = {};
const STOCKS_KEY = ["stocks"];
const MY_SHOPS_STOCKS_KEY = ["my-shops-stocks"];
const SALE_AVAILABLE_STOCK_KEY = ["sale-available-stock"];
const MY_SHOPS_AVAILABLE_STOCK_KEY = ["my-shops-available-stock"];

export const useSaleAvailableStock = (shopId) => {
    const query = useQuery({
        queryKey: [...SALE_AVAILABLE_STOCK_KEY, shopId],
        queryFn: () => stockService.getAvailableForSale(shopId),
        enabled: Boolean(shopId),
        staleTime: 5 * 60 * 1000,
    });

    return {
        stocks: query.data?.data || [],
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,
    };
};

export const useMyShopsAvailableStock = () => {
    const query = useQuery({
        queryKey: MY_SHOPS_AVAILABLE_STOCK_KEY,
        queryFn: () => stockService.getMyShopsAvailableForSale(),
        staleTime: 5 * 60 * 1000,
    });

    return {
        stocks: query.data?.data || [],
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,
    };
};

export const useMyShopsStocks = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [shopFilter, setShopFilter] = useState("");
    const [productFilter, setProductFilter] = useState("");

    const params = useMemo(() => ({
        shopId: shopFilter || undefined,
        productId: productFilter || undefined,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [
        shopFilter,
        productFilter,
        currentPage,
        pageSize,
        searchTerm,
        statusFilter,
        startDate,
        endDate,
        initialParams,
    ]);

    const query = useQuery({
        queryKey: [...MY_SHOPS_STOCKS_KEY, params],
        queryFn: () => stockService.getMyShops(params),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: MY_SHOPS_STOCKS_KEY }),
        [queryClient]
    );

    const createStock = useCallback(async (data) => {
        const response = await stockService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateStock = useCallback(async (id, data) => {
        const response = await stockService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleStockStatus = useCallback(async (id) => {
        const response = await stockService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const getStockWithSales = useCallback(async (id) => {
        const response = await stockService.getWithSales(id);
        return response;
    }, []);

    return {
        stocks: query.data?.data || [],
        pagination: query.data?.meta || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        shopFilter,
        setShopFilter,
        productFilter,
        setProductFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        refresh: query.refetch,
        createStock,
        updateStock,
        toggleStockStatus,
        getStockWithSales,
    };
};

export const useShopStocks = (shopId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [productFilter, setProductFilter] = useState("");

    const params = useMemo(() => ({
        shopId,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        productId: productFilter || undefined,
        ...initialParams,
    }), [shopId, currentPage, pageSize, searchTerm, statusFilter, startDate, endDate, productFilter, initialParams]);

    const query = useQuery({
        queryKey: [...STOCKS_KEY, "shop", shopId, params],
        queryFn: () => stockService.getAll(params),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: STOCKS_KEY }),
        [queryClient]
    );

    const createStock = useCallback(async (data) => {
        const response = await stockService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateStock = useCallback(async (id, data) => {
        const response = await stockService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleStockStatus = useCallback(async (id) => {
        const response = await stockService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const getStockWithSales = useCallback(async (id) => {
        const response = await stockService.getWithSales(id);
        return response;
    }, []);

    return {
        stocks: query.data?.data || [],
        pagination: query.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        productFilter,
        setProductFilter,
        refresh: query.refetch,
        createStock,
        updateStock,
        toggleStockStatus,
        getStockWithSales,
    };
};

export const useStock = useShopStocks;
export const useStocks = useShopStocks;